// @flow

import React from 'react';
import type { Node } from 'react';
import styled, { css } from 'styled-components';
import { parseToRgb, toColorString } from 'polished';
import { media, getBackgroundColor, getColor } from '../../helpers';
import { Flex } from '../grid';
import type { BackgroundImage } from './types';
import type { Theme, ACFCut } from '../../types';
import type { RowColor } from '../../types/acf/row';
import { rowCut, positionSticky } from './helpers';

export type Props = {
  sticky?: boolean,
  backgroundColor?: RowColor,
  backgroundImage?: boolean | BackgroundImage,
  cut?: ACFCut,
  cutColor?: RowColor,
  gradient?: boolean | string,
  children?: Node,
};

// prevent props to be passed after this component
// $FlowFixMe
const CutProps = ({
  template,
  backgroundImage,
  cutColor,
  backgroundColor,
  sticky,
  cut,
  gradient,
  ...props
}) => (
  <Flex {...props} />
);

const getRowCut = (cut: ?ACFCut, cutColor: string) => {
  switch (cut) {
    case 'forward':
      return rowCut(cutColor);
    case 'backward':
      return rowCut(cutColor, true);
    default:
      return null;
  }
};

const getRgbaColorString = (opacity: string | boolean, theme: Theme): string => {
  if (typeof opacity === 'string') {
    return toColorString({
      ...parseToRgb(`${theme.row.gradientColor}`),
      alpha: parseInt(opacity, 10) / 100,
    });
  }
  return '';
};

const withBackgroundImage = (backgroundImage: Object) => css`
  background-image: url('${backgroundImage.xs}');
  position: relative;
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center center;
  ${media.sm(css`
    background-image: url('${backgroundImage.sm}');
  `)}
  ${media.md(css`
    background-image: url('${backgroundImage.md}');
  `)}

  ${media.lg(css`
    background-image: url('${backgroundImage.lg}');
  `)}

  .column {
    min-height: 6rem;
    box-sizing: border-box;
    ${media.md`min-height: 12rem;`}
    ${media.lg`min-height: 16rem;`}
  }
`;

const withGradient = (gradient: string) => css`
  &::before {
    background: linear-gradient(to right, ${gradient}, rgba(255, 255, 255, 0) 65%, rgba(255, 255, 255, 0) 100%);
    position: absolute;
    left: 0;
    content: '';
    top: 0;
    bottom: 0;
    right: 0;
  }
`;

const StyledRow = styled<typeof CutProps, Theme, Props>(CutProps)`
  position: relative;

  ${props => getRowCut(props.cut, getColor(props.cutColor, props.theme))}

  ${props => getBackgroundColor(props.backgroundColor, props.theme)}

  ${props => typeof props.backgroundImage === 'object' && withBackgroundImage(props.backgroundImage)}

  ${props => props.sticky && positionSticky()}

  ${props => props.gradient && withGradient(getRgbaColorString(props.gradient, props.theme))}
`;

export default StyledRow;
